import { narrativePlainText } from "./narrativeHtml";
import {
  buildApiProxyUrl,
  buildPublicBase,
} from "./apiUrls";

export const SHARE_LINK_COPIED_MESSAGE = "Share link copied to clipboard.";
const RAW_PUBLIC_APP_BASE = String(
  process.env.REACT_APP_PUBLIC_APP_BASE ||
  process.env.REACT_APP_PUBLIC_BASE ||
  ""
).trim();

const CONTENT_TYPE_LABELS = {
  job_postings: "Job Posting",
  job_posting: "Job Posting",
  sequences: "Sequence",
  sequence: "Sequence",
  templates: "Template",
  template: "Template",
  pitch_packages: "Pitch Package",
  pitch_package: "Pitch Package",
  proposals: "Proposal",
  proposal: "Proposal",
  fee_agreements: "Fee Agreement",
  fee_agreement: "Fee Agreement",
};

function cleanText(value, maxLength = 160) {
  if (value === undefined || value === null) return "";
  return String(value).replace(/\s+/g, " ").trim().slice(0, maxLength);
}

function pickFirst(...values) {
  for (const value of values) {
    const next = cleanText(value);
    if (next) return next;
  }
  return "";
}

function maybeDistinct(value, ...blockedValues) {
  const next = cleanText(value);
  if (!next) return "";

  const blocked = new Set(
    blockedValues.map((item) => cleanText(item).toLowerCase()).filter(Boolean)
  );
  return blocked.has(next.toLowerCase()) ? "" : next;
}

function toCountLabel(value, singular, plural = `${singular}s`) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue) || numericValue < 0) return "";
  return `${numericValue} ${numericValue === 1 ? singular : plural}`;
}

function buildField(label, ...values) {
  const value = pickFirst(...values);
  if (!value) return null;
  return {
    label: cleanText(label, 40),
    value: cleanText(value, 180),
  };
}

function uniqueList(values = [], maxItems = 6, maxLength = 40) {
  const seen = new Set();
  const normalized = [];

  values.forEach((value) => {
    const next = cleanText(value, maxLength);
    if (!next) return;
    const key = next.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    normalized.push(next);
  });

  return normalized.slice(0, maxItems);
}

function normalizeFields(fields = []) {
  const seen = new Set();
  const normalized = [];

  fields.forEach((field) => {
    const label = cleanText(field?.label, 40);
    const value = cleanText(field?.value, 180);
    if (!label || !value) return;

    const key = `${label.toLowerCase()}::${value.toLowerCase()}`;
    if (seen.has(key)) return;
    seen.add(key);
    normalized.push({ label, value });
  });

  return normalized.slice(0, 6);
}

function parseArrayLike(value) {
  if (Array.isArray(value)) return value;
  if (typeof value === "string") {
    return value
      .split(",")
      .map((item) => cleanText(item))
      .filter(Boolean);
  }
  return [];
}

function getStoredToken() {
  if (typeof window === "undefined") return "";

  const direct =
    window.localStorage.getItem("token") ||
    window.localStorage.getItem("authToken") ||
    window.localStorage.getItem("jwt") ||
    window.localStorage.getItem("accessToken") ||
    window.localStorage.getItem("access_token") ||
    window.localStorage.getItem("idToken") ||
    window.localStorage.getItem("id_token") ||
    window.sessionStorage.getItem("token") ||
    window.sessionStorage.getItem("authToken") ||
    window.sessionStorage.getItem("jwt") ||
    window.sessionStorage.getItem("accessToken") ||
    window.sessionStorage.getItem("access_token") ||
    window.sessionStorage.getItem("idToken") ||
    window.sessionStorage.getItem("id_token");

  if (cleanText(direct, 4000)) {
    return cleanText(direct, 4000);
  }

  const tryJsonKeys = ["user", "auth", "session", "profile"];
  for (const key of tryJsonKeys) {
    try {
      const raw = window.localStorage.getItem(key) || window.sessionStorage.getItem(key);
      if (!raw) continue;

      const parsed = JSON.parse(raw);
      const token =
        parsed?.token ||
        parsed?.accessToken ||
        parsed?.access_token ||
        parsed?.jwt ||
        parsed?.idToken ||
        parsed?.id_token;

      if (cleanText(token, 4000)) {
        return cleanText(token, 4000);
      }
    } catch {
      // Ignore malformed cached auth payloads.
    }
  }

  return "";
}

function getStoredAuthHeaders() {
  const token = cleanText(getStoredToken(), 4000);
  if (!token) return {};

  const rawToken = token.replace(/^Bearer\s+/i, "").trim();

  return {
    Authorization: token.startsWith("Bearer ") ? token : `Bearer ${rawToken}`,
    "x-access-token": rawToken,
  };
}

export function buildPublicShareUrl(publicPath, apiBase) {
  const path = cleanText(publicPath, 4000);
  if (!path) return "";

  const appBase = cleanText(RAW_PUBLIC_APP_BASE, 4000);
  const windowBase =
    typeof window !== "undefined" && window.location?.origin
      ? cleanText(window.location.origin, 4000)
      : "";
  const base = (appBase || windowBase || buildPublicBase(apiBase)).replace(/\/$/, "");
  return /^https?:\/\//i.test(path)
    ? path
    : `${base}${path.startsWith("/") ? path : `/${path}`}`;
}

export async function copyTextToClipboard(value) {
  const text = cleanText(value, 4000);
  if (!text) throw new Error("Nothing to copy.");

  if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  if (typeof document === "undefined") {
    throw new Error("Clipboard is unavailable.");
  }

  const input = document.createElement("textarea");
  input.value = text;
  input.setAttribute("readonly", "readonly");
  input.style.position = "fixed";
  input.style.opacity = "0";
  input.style.pointerEvents = "none";
  document.body.appendChild(input);
  input.focus();
  input.select();

  try {
    const succeeded = document.execCommand("copy");
    if (!succeeded) {
      throw new Error("Clipboard is unavailable.");
    }
  } finally {
    document.body.removeChild(input);
  }
}

async function parseJsonSafe(response) {
  return response.json().catch(() => ({}));
}

function isCandidateSharePayload(payload = {}) {
  return cleanText(payload?.entityType, 40).toLowerCase() === "candidate";
}

async function createCandidateFallbackShareLink({
  apiBase,
  payload,
  headers,
} = {}) {
  const candidateId = cleanText(payload?.sourceId, 80);
  if (!candidateId) {
    throw new Error("This candidate is missing the details needed for a share link.");
  }

  const response = await fetch(
    buildApiProxyUrl(`/candidates/${encodeURIComponent(candidateId)}/share-profile`, apiBase),
    {
      method: "POST",
      headers,
      credentials: "include",
    }
  );

  const data = await parseJsonSafe(response);
  if (!response.ok) {
    throw new Error(data?.error || "Failed to create share link.");
  }

  const publicPath = cleanText(data?.share?.publicPath, 4000);
  const shareUrl = buildPublicShareUrl(publicPath, apiBase);
  if (!shareUrl) {
    throw new Error("The share link could not be created.");
  }

  await copyTextToClipboard(shareUrl);

  return {
    shareUrl,
    share: data?.share || null,
  };
}

function buildCandidateSharePayload(source = {}) {
  const title = pickFirst(source?.full_name, source?.candidate_name, source?.name);
  return {
    entityType: "candidate",
    sourceId: pickFirst(source?.candidate_id, source?.id),
    title,
    subtitle: maybeDistinct(
      pickFirst(source?.candidate_title, source?.title, source?.job_title),
      title
    ),
    badge: pickFirst(source?.candidate_status, source?.status),
    fields: normalizeFields([
      buildField("Company", source?.current_company, source?.company_name),
      buildField("Location", source?.candidate_location, source?.location, source?.current_location),
      buildField("Owner", source?.owner, source?.recruiter_name, source?.assigned_team_member),
      buildField("Stage", source?.stage, source?.candidate_stage),
    ]),
    chips: [],
  };
}

function buildCompanySharePayload(source = {}) {
  const title = pickFirst(source?.name, source?.company_name);
  return {
    entityType: "company",
    sourceId: pickFirst(source?.id, source?.company_id),
    title,
    subtitle: maybeDistinct(
      pickFirst(source?.industry, source?.industry_sector, source?.website, source?.website_url),
      title
    ),
    description: cleanText(source?.full_company_description, 280),
    badge: pickFirst(source?.status),
    fields: normalizeFields([
      buildField("Website", source?.website, source?.website_url, source?.company_website),
      buildField("Location", source?.location),
      buildField("Owner", source?.owner, source?.owner_name, source?.team_member),
      buildField(
        "Open Roles",
        toCountLabel(source?.jobOpenings ?? source?.jobsCount ?? source?.jobs_count, "role")
      ),
      buildField(
        "Contacts",
        toCountLabel(source?.contactsCount ?? source?.contacts_count, "contact")
      ),
    ]),
    chips: uniqueList([source?.industry, source?.industry_sector]),
  };
}

function buildLeadSharePayload(source = {}) {
  const title = pickFirst(source?.lead_name, source?.job_title, source?.name, source?.company_name);
  return {
    entityType: "lead",
    sourceId: pickFirst(source?.id, source?.lead_id, source?.leadId),
    title,
    subtitle: maybeDistinct(
      pickFirst(source?.company_name, source?.title, source?.position, source?.role_title),
      title
    ),
    badge: pickFirst(source?.status),
    fields: normalizeFields([
      buildField("Company", source?.company_name, source?.company, source?.raw?.company_name),
      buildField("Location", source?.location),
      buildField("Owner", source?.owner, source?.owner_name, source?.recruiter_name),
      buildField("Source", source?.source),
    ]),
    chips: [],
  };
}

function buildContactSharePayload(source = {}) {
  const title = pickFirst(source?.name, source?.contact_name);
  return {
    entityType: "contact",
    sourceId: pickFirst(source?.contact_id, source?.hiring_manager_id, source?.id),
    title,
    subtitle: maybeDistinct(pickFirst(source?.role, source?.title), title),
    badge: pickFirst(source?.status),
    fields: normalizeFields([
      buildField("Company", source?.company, source?.company_name, source?.entity_name),
      buildField("Email", source?.email, source?.contact_email),
      buildField("Phone", source?.phone, source?.contact_phone),
      buildField("LinkedIn", source?.linkedin_url, source?.linkedin),
      buildField("Owner", source?.owner, source?.owner_name, source?.team_member),
    ]),
    chips: [],
  };
}

function buildJobSharePayload(source = {}) {
  const title = pickFirst(source?.job_title, source?.title, source?.role_title);
  return {
    entityType: "job",
    sourceId: pickFirst(source?.id, source?.job_id),
    title,
    subtitle: maybeDistinct(
      pickFirst(source?.company_name, source?.linked_company_name, source?.company),
      title
    ),
    badge: pickFirst(source?.status),
    fields: normalizeFields([
      buildField("Company", source?.company_name, source?.linked_company_name, source?.company),
      buildField("Location", source?.location),
      buildField("Opened", source?.date_opened, source?.created_at, source?.opened_at),
    ]),
    chips: uniqueList([source?.work_arrangement, source?.employment]),
  };
}

function normalizeContentTypeLabel(source = {}) {
  const explicit = pickFirst(source?.content_type_label, source?.contentTypeLabel);
  if (explicit) return explicit;

  const key = cleanText(
    source?.content_type_key || source?.contentType || source?.tabId,
    60
  ).toLowerCase();
  return CONTENT_TYPE_LABELS[key] || "Content";
}

function buildContentSharePayload(source = {}) {
  const title = pickFirst(source?.content_name, source?.job_title, source?.name, source?.title);
  const rawTags = [
    ...parseArrayLike(source?.tags),
    ...parseArrayLike(source?.tag_list),
    ...parseArrayLike(source?.content_tags),
  ];

  return {
    entityType: "content",
    sourceId: pickFirst(source?.id),
    title,
    subtitle: normalizeContentTypeLabel(source),
    description: cleanText(
      narrativePlainText(pickFirst(source?.summary, source?.description, source?.excerpt, source?.preview_text)),
      280
    ),
    badge: pickFirst(source?.status),
    fields: normalizeFields([
      buildField("Owner", source?.owner_name, source?.owner),
      buildField("Updated", source?.updated_label, source?.updated_at, source?.last_used_label),
      buildField("Created", source?.created_label, source?.created_at),
    ]),
    chips: uniqueList(rawTags),
  };
}

function buildTeamSharePayload(source = {}) {
  const title = pickFirst(source?.name, source?.full_name, source?.team_member);
  const rolesWorked = uniqueList(parseArrayLike(source?.roles_worked || source?.rolesWorked), 4);
  const experience = uniqueList(
    (Array.isArray(source?.jobs_worked_history) ? source.jobs_worked_history : source?.jobsWorkedHistory || [])
      .map((job) => [job?.title, job?.company].filter(Boolean).join(" at ")),
    4,
    80
  );
  return {
    entityType: "team_member",
    sourceId: pickFirst(source?.id, source?.user_id),
    title,
    subtitle: maybeDistinct(pickFirst(source?.title, source?.job_title), title),
    description: cleanText(source?.about || source?.professional_summary || source?.summary, 280),
    badge: pickFirst(source?.status),
    fields: normalizeFields([
      buildField("Location", source?.location),
      buildField("Manager", source?.owner, source?.manager),
      buildField("Focus", source?.industryExpertise, source?.industry, source?.department),
      buildField("Roles", rolesWorked.join(", ")),
      buildField("Experience", experience.join("; ")),
      buildField("Engagement", source?.engagementType, source?.engagement_type),
      buildField("Assignments", toCountLabel(source?.assignments, "assignment")),
    ]),
    chips: uniqueList(source?.skills || source?.specialties || []),
  };
}

export function buildPublicSharePayload(entityType, source = {}) {
  const normalizedEntityType = cleanText(entityType, 40).toLowerCase();

  switch (normalizedEntityType) {
    case "candidate":
      return buildCandidateSharePayload(source);
    case "company":
      return buildCompanySharePayload(source);
    case "lead":
      return buildLeadSharePayload(source);
    case "contact":
      return buildContactSharePayload(source);
    case "job":
      return buildJobSharePayload(source);
    case "content":
      return buildContentSharePayload(source);
    case "team_member":
      return buildTeamSharePayload(source);
    default:
      throw new Error("Unsupported share type.");
  }
}

export async function createAndCopyPublicShareLink({
  apiBase,
  payload,
} = {}) {
  const candidateShare = isCandidateSharePayload(payload);

  if (!payload?.title && !candidateShare) {
    throw new Error("This record is missing the details needed for a share link.");
  }

  const headers = {
    "Content-Type": "application/json",
    ...getStoredAuthHeaders(),
  };

  if (candidateShare) {
    return createCandidateFallbackShareLink({
      apiBase,
      payload,
      headers,
    });
  }

  const response = await fetch(buildApiProxyUrl("/share-links", apiBase), {
    method: "POST",
    headers,
    credentials: "include",
    body: JSON.stringify(payload),
  });
  const data = await parseJsonSafe(response);

  if (!response.ok) {
    throw new Error(data?.error || "Failed to create share link.");
  }

  const publicPath = cleanText(data?.share?.publicPath, 4000);
  const shareUrl = buildPublicShareUrl(publicPath, apiBase);

  if (!shareUrl) {
    throw new Error("The share link could not be created.");
  }

  await copyTextToClipboard(shareUrl);

  return {
    shareUrl,
    share: data?.share || null,
  };
}
